import type { FormEvent, RefObject } from 'react';
import { Palette, Radio, Send } from 'lucide-react';
import { Button, Card, Tabs, TextField } from '@/components/play';
import type { AgentMode } from './types';

type DashboardHeroSectionProps = {
  firstName: string;
  agentMode: AgentMode;
  setAgentMode: (mode: AgentMode) => void;
  promptValue: string;
  setPromptValue: (value: string) => void;
  isSubmitting: boolean;
  promptInputRef: RefObject<HTMLInputElement>;
  handlePromptSubmit: (event: FormEvent) => void;
};

type PromptFormProps = Pick<
  DashboardHeroSectionProps,
  'promptValue' | 'setPromptValue' | 'isSubmitting' | 'handlePromptSubmit'
> & {
  label: string;
  placeholder: string;
  hint: string;
  /** Only the selected tab's field takes the ref: the leaving panel unmounts after the new one mounts. */
  inputRef?: RefObject<HTMLInputElement>;
};

/** Play has no prompt field with a built-in submit: a labelled TextField and a separate submit Button. */
function PromptForm({
  label,
  placeholder,
  hint,
  promptValue,
  setPromptValue,
  isSubmitting,
  inputRef,
  handlePromptSubmit,
}: PromptFormProps) {
  return (
    <form onSubmit={handlePromptSubmit} className="grid gap-4">
      <TextField
        ref={inputRef}
        label={label}
        value={promptValue}
        onChange={(e) => setPromptValue(e.target.value)}
        placeholder={placeholder}
        description={hint}
        disabled={isSubmitting}
      />
      <Button type="submit" loading={isSubmitting} disabled={!promptValue.trim()} className="justify-self-start">
        <Send aria-hidden="true" />
        Create event
      </Button>
    </form>
  );
}

/** The AI Producer's prompt: a greeting, then the prompt in the chosen mode. */
export function DashboardHeroSection({
  firstName,
  agentMode,
  setAgentMode,
  promptValue,
  setPromptValue,
  isSubmitting,
  promptInputRef,
  handlePromptSubmit,
}: DashboardHeroSectionProps) {
  const prompt = { promptValue, setPromptValue, isSubmitting, handlePromptSubmit };
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-neutral-10">Hi {firstName}, what are you streaming today?</h1>
        <p className="text-base text-neutral-7">Describe your event. We&apos;ll set it up.</p>
      </header>

      <Card>
        <Tabs<AgentMode>
          label="Create mode"
          value={agentMode}
          onValueChange={setAgentMode}
          items={[
            {
              value: 'stream',
              label: 'Stream',
              icon: <Radio aria-hidden="true" />,
              content: (
                <PromptForm
                  {...prompt}
                  inputRef={agentMode === 'stream' ? promptInputRef : undefined}
                  label="Describe your event"
                  placeholder="e.g. Trail ultra with 4 cameras on the course"
                  hint="Sets up cameras and invite links."
                />
              ),
            },
            {
              value: 'design',
              label: 'Design',
              icon: <Palette aria-hidden="true" />,
              content: (
                <PromptForm
                  {...prompt}
                  inputRef={agentMode === 'design' ? promptInputRef : undefined}
                  label="Describe your event and its page"
                  placeholder="e.g. Course map, leaderboard and a camera grid"
                  hint="Also drafts the event page."
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
